import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { Badge, Button, Divider, Modal, Space, Tooltip } from 'antd';

import {
  useDeleteQuotation,
  useQuotations,
} from '@/core/domain/stock/Quotations/Quotations.hooks';
import {
  Quotation,
  QuotationsQuery,
  QuotationStatus,
  QuotationStatusColor,
  QuotationStatusLabel,
  suppliersQuotation,
} from '@/core/domain/stock/Quotations/Quotations.types';

import { PageProps } from '@/types/PageProps';

import AppPageHeader from '@/components/_common/AppPageHeader';

import CreateQuotationModal from '@/components/Movements/Quotations/CreateQuotationModal';
import TabContentWrapper from '@/components/_common/TabContentWrapper';
import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import QuotationsExtraFilters from '@/components/Movements/Quotations/QuotationsExtraFilters';
import { getTableScrollX } from '@/utils/table';
import EditIcon from '@/components/_common/EditIcon';
import { DeleteIcon, Group } from '@/components/_common';
import { useDebounce } from '@/hooks';
import StatusTag from '@/components/_common/StatusTag';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import usePageSearchHistory from '@/hooks/pageSearchHistory';

const QuotationsPage = ({ route }: PageProps) => {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<QuotationsQuery>({
    order: 'DESC',
    ...convertParams<QuotationsQuery>({
      search: 'string',
      date: 'string',
      workOrderNumber: 'string',
      licensePlate: 'string',
      status: 'string',
      order: 'string',
    }),
  });

  const [createModalVisible, setCreateModalVisible] = useState(false);

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { setLocation } = usePageSearchHistory();
  useEffect(setLocation, [queryDebounce]);

  const { data, isFetching, hasNextPage, fetchNextPage } =
    useQuotations(queryDebounce);

  const quotations = flatInfiniteResult(data);

  const handleCreateModalVisible = () => {
    setCreateModalVisible(!createModalVisible);
  };
  const deleteMutation = useDeleteQuotation();

  const handleRemoveClick = (quotation: Quotation) => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'quotations.remove' }),
      content: `${intl.formatMessage({
        id: 'quotations.remove.alert',
      })} ${quotation?.company.name}, ${moment(quotation?.date).format(
        'DD/MM/YYYY',
      )}`,
      onOk: async () => {
        // Fetch Service to remove area item.
        await deleteMutation.mutateAsync(quotation.id);
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<Quotation> = [
    {
      dataIndex: 'number',
      width: 50,
      ellipsis: true,
      title: intl.formatMessage({
        id: 'quotations.list.table.title.code',
      }),
    },
    {
      dataIndex: ['employee', 'name'],
      width: 150,
      ellipsis: true,
      title: intl.formatMessage({
        id: 'quotations.list.table.title.employeeName',
      }),
    },
    {
      dataIndex: ['company', 'name'],
      width: 150,
      ellipsis: true,
      title: intl.formatMessage({
        id: 'quotations.list.table.title.companyName',
      }),
    },
    {
      key: 'date',
      dataIndex: 'date',
      width: 150,
      ellipsis: true,
      title: intl.formatMessage({
        id: 'quotations.list.table.title.date',
      }),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
    {
      key: 'status',
      dataIndex: 'status',
      width: 150,
      ellipsis: true,
      title: intl.formatMessage({
        id: 'quotations.list.table.title.status',
      }),
      render: (value: QuotationStatus) => (
        <StatusTag
          color={QuotationStatusColor[value]}
          label={intl.formatMessage({
            id: `quotations.${QuotationStatusLabel[value]}`,
          })}
        />
      ),
    },
    {
      key: 'suppliers',
      dataIndex: 'suppliers',
      title: 'Fornecedor',
      align: 'center',
      width: 200,
      ellipsis: true,
      render: (suppliers: suppliersQuotation[]) => {
        if (suppliers?.length === 0) {
          return 'Não há fornecedores selecionados para essa cotação.';
        }

        if (suppliers?.length === 1) {
          return suppliers[0].name;
        }

        const suppliersName = suppliers.map((supplier) => supplier.name);

        return (
          <>
            {suppliersName[0]}{' '}
            {suppliersName.length > 1 && (
              <Tooltip
                title={suppliers?.slice(1)?.map((supplier, i) => (
                  <Group
                    key={supplier.id}
                    style={{
                      marginBottom: i + 1 < suppliers?.length ? '8px' : 0,
                    }}
                  >
                    <div>{supplier.name}</div>
                  </Group>
                ))}
              >
                <Badge
                  count={`+${suppliersName.slice(1).length}`}
                  style={{
                    backgroundColor: '#364068',
                  }}
                />
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      key: 'action',
      width: 90,
      align: 'right',
      fixed: 'right',
      ellipsis: true,
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      render: (row, record) => (
        <Space>
          <EditIcon
            redirect={`/app/movements/quotations/${row.id}/items`}
            disabled={
              !hasPermissions([Authorities.MOVIMENTATION_QUOTATION_EDIT])
            }
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(row)}
            disabled={
              record.status !== QuotationStatus.Configuring ||
              !hasPermissions([Authorities.MOVIMENTATION_QUOTATION_DELETE])
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <TabContentWrapper
        title={route.name}
        permissions={route.permissions}
        extra={
          <Button
            type="primary"
            onClick={handleCreateModalVisible}
            disabled={
              !hasPermissions([Authorities.MOVIMENTATION_QUOTATION_ADD])
            }
            block
          >
            {intl.formatMessage({
              id: 'quotations.list.button.newQuotation',
            })}
          </Button>
        }
      >
        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, search: value }))
          }
          searchValue={queryParams.search}
          headerActions={({ isShowingFilters }) =>
            isShowingFilters && (
              <Button
                type="primary"
                onClick={() => setQueryParams({ order: 'DESC' })}
              >
                {intl.formatMessage({ id: 'commons.clear.filters' })}
              </Button>
            )
          }
          extraFilters={
            <QuotationsExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />

        <InfiniteTable<Quotation>
          size="small"
          loading={isFetching}
          rowKey={(row) => row.id}
          dataSource={quotations}
          columns={columns}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          scroll={{ x: getTableScrollX(columns) }}
        />
      </TabContentWrapper>

      <CreateQuotationModal
        visible={createModalVisible}
        handleVisible={handleCreateModalVisible}
      />
    </>
  );
};

export default QuotationsPage;
